import { QuotingPortal } from 'lib/hosts/quoting/interface';
import { Maybe, Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

export const EmergeMarketSubmitAction = 'emergemarket-submit-quote';
const EmergeMarketDomain = 'emergemarket.io';
const EmergeMarketSpotPathRegex =
  /^\/cp\/source\/spot\/(open|submitted|closed)(\/.*)?$/i;
const EmergeMarketSpotListPathRegex =
  /^\/cp\/source\/spot\/(open|submitted|closed)\/?$/i;
const EmergeMarketSpotDetailPathRegex =
  /^\/cp\/source\/spot\/(open|submitted|closed)\/\d+\/?$/i;
const EmergeMarketSpotClosedPathRegex = /^\/cp\/source\/spot\/closed(\/.*)?$/i;
const EmergeMarketTrackingPathRegex = /^\/cp\/manage\/tracking\/\d+\/?$/i;
const EmergeMarketTrackingListPathRegex = /^\/cp\/manage\/tracking\/?$/i;

export function isEmergeMarketHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase();

  return (
    normalized === EmergeMarketDomain ||
    normalized.endsWith(`.${EmergeMarketDomain}`)
  );
}

export function isEmergeMarketSpotPath(pathname: string): boolean {
  return EmergeMarketSpotPathRegex.test(pathname);
}

export function isEmergeMarketSpotListPath(pathname: string): boolean {
  return EmergeMarketSpotListPathRegex.test(pathname);
}

export function isEmergeMarketSpotDetailPath(pathname: string): boolean {
  return EmergeMarketSpotDetailPathRegex.test(pathname);
}

export function isEmergeMarketSpotClosedPath(pathname: string): boolean {
  return EmergeMarketSpotClosedPathRegex.test(pathname);
}

/**
 * URL path for tendered loads that are ready for user to build into their TMS
 */
export function isEmergeMarketTrackingPath(pathname: string): boolean {
  return EmergeMarketTrackingPathRegex.test(pathname);
}

export function isEmergeLoadBuildingListPath(pathname: string): boolean {
  return EmergeMarketTrackingListPathRegex.test(pathname);
}

/** Emerge spot / Quick Quote pages. */
export function isEmergeQuoteRequestUrl(urlStr: Undef<string>): boolean {
  if (!urlStr) {
    return false;
  }

  try {
    const parsed = new URL(urlStr);

    return (
      isEmergeMarketHost(parsed.hostname) &&
      isEmergeMarketSpotPath(parsed.pathname)
    );
  } catch {
    return false;
  }
}

/**
 * URL path for tendered loads that are ready for user to build into their TMS
 */
export function isEmergeLoadBuildingUrl(urlStr: Undef<string>): boolean {
  if (!urlStr) {
    return false;
  }

  try {
    const parsed = new URL(urlStr);
    return (
      isEmergeMarketHost(parsed.hostname) &&
      isEmergeMarketTrackingPath(parsed.pathname)
    );
  } catch {
    return false;
  }
}

export function emergeSpotBidFormPresentInHtml(
  html: string,
  pathname: string
): boolean {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const isListSpotPath = isEmergeMarketSpotListPath(pathname);
    const isLikelyVisible = (el: Element): boolean => {
      let current: Maybe<Element> = el;

      while (current) {
        const style = (current.getAttribute('style') ?? '').toLowerCase();
        const className = (current.getAttribute('class') ?? '').toLowerCase();
        if (
          current.hasAttribute('hidden') ||
          current.getAttribute('aria-hidden') === 'true' ||
          /\bmui[a-z-]*-hidden\b/.test(className) ||
          /display\s*:\s*none/.test(style) ||
          /visibility\s*:\s*hidden/.test(style) ||
          /opacity\s*:\s*0(?:[;)\s]|$)/.test(style)
        ) {
          return false;
        }

        current = current.parentElement;
      }

      return true;
    };

    const rateInputs = Array.from(
      doc.querySelectorAll<HTMLInputElement>(
        'input[data-testid="base-rate"], input[name="capacityProviderRate"]'
      )
    );

    for (const rateInput of rateInputs) {
      if (!isLikelyVisible(rateInput)) {
        continue;
      }

      const dialogRoot = rateInput.closest(
        '[role="dialog"], [aria-modal="true"], .MuiDialog-root, .MuiModal-root'
      );

      // On list pages, only treat popup modal input as actionable.
      if (isListSpotPath && (!dialogRoot || !isLikelyVisible(dialogRoot))) {
        continue;
      }

      const isRateInputEnabled =
        !rateInput.hasAttribute('disabled') &&
        !rateInput.hasAttribute('readonly') &&
        rateInput.getAttribute('aria-disabled') !== 'true';

      if (!isRateInputEnabled) {
        continue;
      }

      // Prefer a submit control in the same dialog/root to avoid hidden/stale matches.
      const nearestRoot = (dialogRoot ??
        rateInput.parentElement ??
        doc.body) as ParentNode;

      const submitControl =
        nearestRoot.querySelector('button[data-testid="submit-button"]') ??
        nearestRoot.querySelector('button[data-testid="submit-bid-button"]') ??
        doc.querySelector('button[data-testid="submit-button"]') ??
        doc.querySelector('button[data-testid="submit-bid-button"]');

      if (submitControl && isLikelyVisible(submitControl)) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
}

export class EmergeMarket implements QuotingPortal {
  submitAction = EmergeMarketSubmitAction;

  canSubmit(tab: Undef<chrome.tabs.Tab>, html: string): boolean {
    if (!tab?.url) {
      return false;
    }

    try {
      const url = new URL(tab.url);
      if (!isEmergeMarketHost(url.hostname)) {
        return false;
      }

      if (isEmergeMarketSpotClosedPath(url.pathname)) {
        return false;
      }

      const isSpotPath = isEmergeMarketSpotPath(url.pathname);
      if (!isSpotPath) {
        return false;
      }

      // Consider submittable only when the bid form itself is present in the DOM snapshot.
      // (List "Submit Bid/Edit Bid" openers are handled by background submit logic once clicked.)
      return emergeSpotBidFormPresentInHtml(html, url.pathname);
    } catch (error: unknown) {
      captureException(error, { context: 'EmergeMarket canSubmit' });
      return false;
    }
  }

  additionalInfo(tab: Undef<chrome.tabs.Tab>, html: string): Maybe<string> {
    if (!tab?.url) {
      return null;
    }

    try {
      const url = new URL(tab.url);

      if (!isEmergeMarketHost(url.hostname)) {
        return null;
      }

      if (isEmergeMarketSpotClosedPath(url.pathname)) {
        return 'Submission is not available for closed loads.';
      }

      if (isEmergeMarketSpotListPath(url.pathname)) {
        if (!emergeSpotBidFormPresentInHtml(html, url.pathname)) {
          return 'Expand a load, open details, then use Submit to Portal from the full load page when the bid field is visible.';
        }

        return null;
      }

      if (
        isEmergeMarketSpotDetailPath(url.pathname) &&
        !emergeSpotBidFormPresentInHtml(html, url.pathname)
      ) {
        return 'Scroll to the bid section (Submit a bid / Edit a bid) and wait for the form to load to submit from Drumkit.';
      }
    } catch (error: unknown) {
      captureException(error, { context: 'EmergeMarket additionalInfo' });
    }

    return null;
  }
}
